import {
  type DateType,
  dateAdd,
  getEndOfDay,
  getLastDayOfMonth,
  getToday,
  isDateBefore,
  isValidDate,
} from 'commons/utils/date'
import { t } from 'i18next'
import * as yup from 'yup'

import type { CalendarCreationModalDefaultValues } from './CalendarCreationModal'
import { DATE_FORMAT, ISO_FORMAT } from './constants'

const INTERVAL_PAIRS = [
  ['havaBeginDate', 'havaEndDate'],
  ['portalBeginDate', 'portalEndDate'],
  ['approvalBeginDate', 'approvalEndDate'],
] as const

const isValid = (value: DateType) => {
  return isValidDate(value, DATE_FORMAT)
}

const notInPast = (value: DateType) => {
  return !isDateBefore(getEndOfDay(value, DATE_FORMAT), getToday())
}

const isBeforeOrEqual = (date: DateType, compareDate: DateType) => {
  return isDateBefore(date, compareDate) || date === compareDate
}

export const getCalendarValidationSchema = (
  isEdit: boolean,
  calendarValues?: CalendarCreationModalDefaultValues,
) => {
  const initialDates: Record<string, string | null> = {
    approvalBeginDate: calendarValues?.approvalBeginDate || null,
    approvalEndDate: calendarValues?.approvalEndDate || null,
    fairShareMonth: calendarValues?.fairShareMonth || null,
    havaBeginDate: calendarValues?.havaBeginDate || null,
    havaEndDate: calendarValues?.havaEndDate || null,
    portalBeginDate: calendarValues?.portalBeginDate || null,
    portalEndDate: calendarValues?.portalEndDate || null,
  }

  const validateDateField = (fieldKey: string) => {
    const initialValue = initialDates[fieldKey]
    return (value: string | null | undefined) => {
      if (!value) return true
      if (!isValid(value)) return false
      if (isEdit && value === initialValue) {
        return true
      }
      return notInPast(value)
    }
  }

  const validateRequired = (fieldKey: string, pairFieldKey: string) => {
    const initialValue = initialDates[fieldKey]
    return function (this: yup.TestContext, value: string | null | undefined) {
      if (isEdit && initialValue && !value) {
        return false
      }
      const pairValue = this.parent[pairFieldKey]
      if (!value && pairValue) return false
      return true
    }
  }

  const validatePeriod = (pairFieldKey: string, position: 'begin' | 'end') => {
    return function (this: yup.TestContext, value: string | null | undefined) {
      const pairValue = this.parent[pairFieldKey]
      if (!value && !pairValue) return true
      if (position === 'begin') {
        return isBeforeOrEqual(value, pairValue)
      }
      return isBeforeOrEqual(pairValue, value)
    }
  }

  const validateFairShareMonth = () => {
    return function (this: yup.TestContext, value: string | null | undefined) {
      const initialValue = initialDates.fairShareMonth
      if (!value) return false
      if (!isValid(value)) return false
      if (isEdit && value === initialValue) {
        return true
      }
      return (
        notInPast(getLastDayOfMonth(value, DATE_FORMAT)) &&
        isBeforeOrEqual(value, dateAdd(getToday(), 3, 'years', DATE_FORMAT, ISO_FORMAT))
      )
    }
  }

  return yup
    .object({
      approvalBeginDate: yup
        .string()
        .nullable()
        .test(
          'is-valid-date',
          t('common.validations.invalidDate'),
          validateDateField('approvalBeginDate'),
        )
        .test(
          'is-required',
          t('common.validations.required'),
          validateRequired('approvalBeginDate', 'approvalEndDate'),
        )
        .test(
          'is-valid-period',
          t('common.validations.invalidPeriod'),
          validatePeriod('approvalEndDate', 'begin'),
        ),
      approvalEndDate: yup
        .string()
        .nullable()
        .test(
          'is-valid-date',
          t('common.validations.invalidDate'),
          validateDateField('approvalEndDate'),
        )
        .test(
          'is-required',
          t('common.validations.required'),
          validateRequired('approvalEndDate', 'approvalBeginDate'),
        )
        .test(
          'is-valid-period',
          t('common.validations.invalidPeriod'),
          validatePeriod('approvalBeginDate', 'end'),
        ),
      fairShareMonth: yup
        .string()
        .nullable()
        .required(t('common.validations.required'))
        .test('is-valid-date', t('common.validations.invalidDate'), validateFairShareMonth()),
      havaBeginDate: yup
        .string()
        .nullable()
        .test(
          'is-valid-date',
          t('common.validations.invalidDate'),
          validateDateField('havaBeginDate'),
        )
        .test(
          'is-required',
          t('common.validations.required'),
          validateRequired('havaBeginDate', 'havaEndDate'),
        )
        .test(
          'is-valid-period',
          t('common.validations.invalidPeriod'),
          validatePeriod('havaEndDate', 'begin'),
        ),
      havaEndDate: yup
        .string()
        .nullable()
        .test(
          'is-valid-date',
          t('common.validations.invalidDate'),
          validateDateField('havaEndDate'),
        )
        .test(
          'is-required',
          t('common.validations.required'),
          validateRequired('havaEndDate', 'havaBeginDate'),
        )
        .test(
          'is-valid-period',
          t('common.validations.invalidPeriod'),
          validatePeriod('havaBeginDate', 'end'),
        ),
      officeCode: yup.string().nullable().required(t('common.validations.required')),
      portalBeginDate: yup
        .string()
        .nullable()
        .test(
          'is-valid-date',
          t('common.validations.invalidDate'),
          validateDateField('portalBeginDate'),
        )
        .test(
          'is-required',
          t('common.validations.required'),
          validateRequired('portalBeginDate', 'portalEndDate'),
        )
        .test(
          'is-valid-period',
          t('common.validations.invalidPeriod'),
          validatePeriod('portalEndDate', 'begin'),
        ),
      portalEndDate: yup
        .string()
        .nullable()
        .test(
          'is-valid-date',
          t('common.validations.invalidDate'),
          validateDateField('portalEndDate'),
        )
        .test(
          'is-required',
          t('common.validations.required'),
          validateRequired('portalEndDate', 'portalBeginDate'),
        )
        .test(
          'is-valid-period',
          t('common.validations.invalidPeriod'),
          validatePeriod('portalBeginDate', 'end'),
        ),
    })
    .test('at-least-one-valid-interval', t('common.validations.atLeastOnePeriod'), (values) => {
      if (!values) return false
      return INTERVAL_PAIRS.some(([begin, end]) => {
        const b = values[begin]
        const e = values[end]
        return isValid(b) && isValid(e) && isBeforeOrEqual(b, e)
      })
    })
}
